import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MetadataService } from 'src/app/services/metadata.service';
import { VideoService } from 'src/app/services/video.service';
import { AppComponent } from 'src/app/app.component';
import { Utils } from 'src/app/services/util.service';
import { AccountService } from 'src/app/services/account.service';
import { Subject } from 'rxjs';
import { UiService } from 'src/app/services/ui.service';
import { ChannelResponse } from 'src/app/models';
import { CanDeactivateComponent } from '../../guards/can-deactivate-guard';
import { ShakaPlayerComponent } from '../../components/shaka-player/shaka-player.component';
import { HttpErrorResponse } from '@angular/common/http';
import { RemoteConfigService } from '../../services/remote-config.service';
import { environment } from '../../../environments/environment';

@Component({
	selector: 'app-video',
	templateUrl: './video.component.html',
	styleUrls: ['./video.component.scss']
})
export class VideoComponent implements OnInit, OnDestroy, CanDeactivateComponent {

	mediaId: number;
	seasonId: number;
	order: string = "asc";
	watchList: boolean = false;
	channel: ChannelResponse;
	media: any[];

	title: string;
	description: string;
	shortDescription: string;
	published: string;
	audioOnlyOption: boolean = false;
	authed: boolean = false;

	// params for player
	dashVideoId: number;
	dashUrl: string;
	hlsVideoId: number;
	previewsUrl: string;
	hlsUrl: string;
	poster: string;
	type: string;
	startTimeMs: number;
	dateValue: string;
	uid: string;
	fpsHlsUrl: string;
	widevineUrl: string;
	fairplayUrl: string;
	textTracks: any;

	autoPlay = true;
    deviceLimitError = false;

	destroy$: Subject<void> = new Subject<void>();

    @ViewChild(ShakaPlayerComponent) shakaPlayerComponent;

	constructor(
		private app: AppComponent,
		private ui: UiService,
		private router: Router,
		private route: ActivatedRoute,
		public accountService: AccountService,
		private metadataService: MetadataService,
		private utils: Utils,
		private videoService: VideoService,
        public config: RemoteConfigService) {

		this.authed = app.isAuthenticated();

		this.mediaId = +this.route.snapshot.paramMap.get('id');

		const autoPlayStorage = localStorage.getItem('autoplay');
		if (autoPlayStorage) {
			this.autoPlay = JSON.parse(autoPlayStorage);
		}

		router.routeReuseStrategy.shouldReuseRoute = () => {
			return false;
		};
	}


	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	async ngOnInit() {
		this.mediaId = +this.route.snapshot.paramMap.get('id');
		this.seasonId = +this.route.snapshot.queryParamMap.get('seasonId');

		this.order = this.route.snapshot.queryParamMap.get('order');

		if (!this.order) {
			this.order = 'asc';
		}

		if (this.route.snapshot.queryParamMap.get('watchlist')) {
            this.watchList = true;
        }
		this.videoService.refreshUpNext(this.mediaId, this.seasonId, this.order, this.watchList);

		this.metadataService.getMetadata(this.mediaId).subscribe({
			next: res => {
				this.title = res.metadata.title;

				this.description = res.metadata.description;
				this.shortDescription = res.metadata.shortDescription;
				this.published = res.metadata.datePublished;
				this.dateValue = this.utils.getTime(res.metadata.datePublished);
				this.audioOnlyOption = res.metadata.audioOnlyOption;
			},
			error: err => {
				if (err.status === 404) {
					this.router.navigate(['/not-found']);
				} else {
					this.router.navigate(['/']);
				}
			}
		});

		let platform = environment.samsung ? 'lg' : 'samsung';

		if (this.app.hasSubscription()) {
			this.accountService.updateToken().then(
				() => {
					this.videoService.getVideo(this.mediaId, platform).subscribe({
						next: res => {
							if (res != null && res.dashUrl != null) {
								this.setLocalVariablesFromVideo(res);
							} else {
								this.router.navigate(['/coming-soon']);
							}
						},
						error: err => {
							if (err.status === 404) {
                                this.router.navigate(['/not-found']);
                            } else if (err.status === 400) {
                                this.handle400(err);
                                throw err;
                            } else {
                                this.router.navigate(['/']);
                            }
							this.accountService.checkUnauthorized(err);
						}
					});
				},
				error => {
				});
		} else {
			if (this.app.isAuthenticated()) {
				this.videoService.getFreeVideo(this.mediaId, platform).subscribe({
					next: value => {
						if (value != null && value.dashUrl != null) {
							this.setLocalVariablesFromVideo(value);
						}
					},
					error: (err) => {
                        if (err.status === 400) {
                            this.handle400(err);
                            throw err;
                        }
						this.accountService.nextRoute = this.router.url;
						this.router.navigate(['home'], { queryParams: { subscriptionRequired: true } });
					}
				});
			} else {
				this.videoService.getFreeVideo(this.mediaId, platform).subscribe({
					next: value => {
						if (value != null && value.dashUrl != null) {
							this.setLocalVariablesFromVideo(value);
						}
					},
					error: (err) => {
                        if (err.status === 400) {
                            this.handle400(err);
                            throw err;
                        }
						this.accountService.nextRoute = this.router.url;
						this.router.navigate(['home']);
						this.ui.setGenericModalSettings({
							title: "Subscription Required",
							body: "A subscription is required to watch this piece of content.",
							close: true
						});
						this.ui.showGenericModal();
					}
				});
			}
		}
	}

    returnToPreviousPage() {
        this.router.navigateByUrl(this.app.previousUrl);
    }

    handle400(error: HttpErrorResponse) {
        if (error.error['device-identifier']) {
            this.deviceLimitError = true;
        }
    }

    canDeactivate(): boolean {
        if (this.shakaPlayerComponent) {
            if (this.shakaPlayerComponent.showReportMediaModal) {
                this.shakaPlayerComponent.handleBackKey();
                return false;
            }
        }
        return true;
    }

    setLocalVariablesFromVideo(videoResult: any): void {
		this.hlsUrl = videoResult.hlsUrl;
		this.hlsVideoId = videoResult.hlsVideoId ?? videoResult.videoId;
		this.dashVideoId = videoResult.dashVideoId ?? videoResult.videoId;
		this.dashUrl = videoResult.dashUrl;
		this.poster = videoResult.poster;
		this.uid = videoResult.uid;
		this.widevineUrl = videoResult.widevineUrl;
		this.fairplayUrl = videoResult.fairPlayUrl;
		this.fpsHlsUrl = videoResult.fpsHlsUrl;
		this.previewsUrl = videoResult.previewsUrl;
		this.textTracks = videoResult.subtitles;
		this.startTimeMs = videoResult.startTimeMs;
	}
}
